/*
 * Carbisdale - NavigationHistory
 *
 * Browser-ähnliche Navigation-History (Zurück/Vorwärts) für Section-Link-Sprünge.
 * Stack enthält alle besuchten Positionen, _index zeigt auf die aktuelle.
 * navigate() für Link-Sprünge, back()/forward() für Alt+Pfeiltasten.
 */

/**
 * NavigationHistory-Klasse
 * Verwaltet eine Zurück/Vorwärts-History für Cursor-Positionen.
 */
export class NavigationHistory {
    /**
     * @param {number} maxSize - Maximale Anzahl Einträge (default 50)
     */
    constructor(maxSize = 50) {
        this._stack = []    // Alle besuchten Positionen
        this._index = -1    // Aktuelle Position im Stack (-1 = leer)
        this._maxSize = maxSize
    }

    /**
     * Registriert einen Sprung von einer Position zu einer anderen.
     * Speichert Quelle (aktualisiert oder neu) und Ziel im Stack.
     *
     * @param {{paragraphId: number, field: string, offset: number, viewportStart: number}} from
     * @param {{paragraphId: number, field: string, offset: number, viewportStart: number}} to
     */
    navigate(from, to) {
        if (this._stack.length === 0) {
            // Erster Sprung: Quelle als neuen Eintrag hinzufügen
            this._stack.push({ ...from })
            this._index = 0
        } else {
            // Folge-Sprung: Aktuelle Position aktualisieren (Cursor/Viewport können sich geändert haben)
            this._stack[this._index] = { ...from }
        }

        // Forward-Einträge abschneiden
        this._stack.length = this._index + 1

        // Ziel hinzufügen
        this._stack.push({ ...to })
        this._index = this._stack.length - 1

        // Maximale Größe einhalten
        if (this._stack.length > this._maxSize) {
            this._stack.shift()
            this._index--
        }
    }

    /**
     * Geht einen Schritt zurück.
     * Speichert optional die aktuelle Position (falls Cursor/Viewport sich seit dem Landen geändert haben).
     *
     * @param {{paragraphId: number, field: string, offset: number, viewportStart: number}} [currentPos]
     * @returns {{paragraphId: number, field: string, offset: number, viewportStart: number}|null}
     */
    back(currentPos) {
        if (!this.canGoBack()) return null
        if (currentPos) this._stack[this._index] = { ...currentPos }
        this._index--
        return { ...this._stack[this._index] }
    }

    /**
     * Geht einen Schritt vorwärts.
     * Speichert optional die aktuelle Position.
     *
     * @param {{paragraphId: number, field: string, offset: number, viewportStart: number}} [currentPos]
     * @returns {{paragraphId: number, field: string, offset: number, viewportStart: number}|null}
     */
    forward(currentPos) {
        if (!this.canGoForward()) return null
        if (currentPos) this._stack[this._index] = { ...currentPos }
        this._index++
        return { ...this._stack[this._index] }
    }

    /**
     * Low-Level Push (für Tests und direkte Nutzung).
     * Entfernt Forward-Einträge und fügt neuen Eintrag hinzu.
     */
    push(paragraphId, field, offset, viewportStart) {
        this._stack.length = this._index + 1
        this._stack.push({ paragraphId, field, offset, viewportStart: viewportStart ?? 0 })
        this._index = this._stack.length - 1
        if (this._stack.length > this._maxSize) {
            this._stack.shift()
            this._index--
        }
    }

    /**
     * @returns {boolean} true wenn zurück möglich
     */
    canGoBack() {
        return this._index > 0
    }

    /**
     * @returns {boolean} true wenn vorwärts möglich
     */
    canGoForward() {
        return this._index < this._stack.length - 1
    }

    /**
     * Leert die gesamte History.
     */
    clear() {
        this._stack = []
        this._index = -1
    }
}
